import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AI_CONFIG } from '../config/aiConfig';

const starterPrompt = `You are an AI solution advisor. Given a short business context, propose 3 actionable AI features for a portfolio website that increase engagement or lead capture. Output as bullet points with a short title and 1 sentence description. Label each with impact: low/medium/high.`;

const AIProjectAdvisor: React.FC = () => {
  const [context, setContext] = useState('I want to showcase AI services and convert visitors to leads.');
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState<string>('');

  const handleGenerate = async () => {
    if (!context.trim()) return;
    setLoading(true);
    setOutput('');

    try {
      const directApiKey = process.env.REACT_APP_DEEPSEEK_API_KEY;
      
      // If we have a direct API key, use it immediately
      if (directApiKey) {
        const response = await fetch('https://api.deepseek.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${directApiKey}`
          },
          body: JSON.stringify({
            model: AI_CONFIG.model,
            temperature: 0.4,
            max_tokens: 400,
            messages: [
              { role: 'system', content: starterPrompt },
              { role: 'user', content: `Business context: ${context}` }
            ]
          })
        });

        if (!response.ok) {
          throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        const text = data?.choices?.[0]?.message?.content || 'No suggestions produced.';
        setOutput(text);
        return;
      }

      // Fallback: try serverless function if no direct API key
      if (!AI_CONFIG.apiEndpoint) {
        throw new Error('No AI backend configured');
      }

      const response = await fetch(AI_CONFIG.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: AI_CONFIG.model,
          temperature: 0.4,
          max_tokens: 400,
          messages: [
            { role: 'system', content: starterPrompt },
            { role: 'user', content: `Business context: ${context}` }
          ]
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const text = data?.choices?.[0]?.message?.content || 'No suggestions produced.';
      setOutput(text);
    } catch (e: any) {
      setOutput(
        `Bonjour ! L'outil AI Project Advisor n'a pas accès à un backend IA sur cet hébergement. ` +
        `Contact rapide via Upwork: https://www.upwork.com/freelancers/~01902b7448e204d256?mp_source=share ` +
        `(${e?.message || 'Unknown error'})`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow p-6 space-y-4">
      <h3 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">AI Project Advisor</h3>
      <p className="text-gray-600 dark:text-gray-300">Describe your goal or audience and get 3 AI features to add.</p>
      <textarea
        value={context}
        onChange={(e) => setContext(e.target.value)}
        rows={4}
        className="w-full p-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
      />
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        disabled={loading}
        onClick={handleGenerate}
        className="px-5 py-2 rounded-xl text-white bg-gradient-to-r from-blue-700 to-blue-500 disabled:opacity-60"
      >
        {loading ? 'Generating…' : 'Generate AI Suggestions'}
      </motion.button>
      {output && (
        <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap">{output}</div>
      )}
    </div>
  );
};

export default AIProjectAdvisor;
export {};


